import React from 'react';
import { PolicyTemplate } from '../../../types/policy';
import { ParameterForm } from '../../../types/policyEditor';

/**
 * Props interface for the ParametersTab component
 * @property policy - The current policy template being edited
 * @property parameterForm - Form state for parameter editing
 * @property editingParameter - Name of parameter currently being edited (null if adding new)
 * @property nameError - Error message for parameter name validation
 * @property allowedValuesError - Error message for allowed values validation
 * @property defaultValueError - Error message for default value validation
 * @property onParameterFormChange - Callback for form field updates
 * @property onAddParameter - Callback for parameter submission
 * @property onEditParameter - Callback for editing existing parameters
 * @property onRemoveParameter - Callback for removing parameters
 * @property onAddClick - Callback for adding a new parameter
 * @property onCancelEdit - Callback for canceling parameter editing
 * @property onLiveParameterUpdate - Callback for live parameter updates
 */
interface ParametersTabProps {
  policy: PolicyTemplate;
  parameterForm: ParameterForm;
  editingParameter: string | null;
  nameError: string;
  allowedValuesError: string;
  defaultValueError: string;
  onParameterFormChange: (updates: Partial<ParameterForm>) => void;
  onAddParameter: (e: React.FormEvent) => void;
  onEditParameter: (name: string, param: any) => void;
  onRemoveParameter: (name: string) => void;
  onAddClick: () => void;
  onCancelEdit: () => void;
  onLiveParameterUpdate?: (name: string, parameter: any) => void;
}

/**
 * ParametersTab component for managing policy parameters
 * 
 * This component provides a complete interface for managing policy parameters, including:
 * - Adding new parameters with various types
 * - Editing existing parameters
 * - Setting allowed values and default values
 * - Parameter validation
 * - Type-specific input handling
 */
export const ParametersTab: React.FC<ParametersTabProps> = ({
  policy,
  parameterForm,
  editingParameter,
  nameError,
  allowedValuesError,
  defaultValueError,
  onParameterFormChange,
  onAddParameter,
  onEditParameter,
  onRemoveParameter,
  onAddClick,
  onCancelEdit,
  onLiveParameterUpdate
}) => {
  /**
   * Handles form field changes with live updates to policy JSON
   * Updates both the form state and triggers live parameter update
   */
  const handleFormChangeWithLiveUpdate = (updates: Partial<ParameterForm>) => {
    onParameterFormChange(updates);
    
    // Trigger live update if we have a parameter being edited and live update callback
    if (editingParameter && onLiveParameterUpdate) {
      const updatedForm = { ...parameterForm, ...updates };
      
      try {
        // Create the parameter object
        const parameter = {
          type: updatedForm.type,
          metadata: {
            displayName: updatedForm.displayName,
            description: updatedForm.description
          },
          ...(updatedForm.allowedValues[0] && {
            allowedValues: JSON.parse(updatedForm.allowedValues[0])
          }),
          defaultValue: updatedForm.type === 'object'
            ? (updatedForm.defaultValue ? JSON.parse(updatedForm.defaultValue) : {})
            : updatedForm.type === 'array'
              ? (updatedForm.defaultValue ? JSON.parse(updatedForm.defaultValue) : [])
              : updatedForm.type === 'integer' || updatedForm.type === 'float'
                ? Number(updatedForm.defaultValue)
                : updatedForm.defaultValue
        };

        // Always update the current editing parameter key, never change the key during live editing
        onLiveParameterUpdate(editingParameter, parameter);
      } catch (error) {
        // Ignore JSON parsing errors for live updates
        console.debug('Skipping live update due to invalid JSON:', error);
      }
    }
  };

  /**
   * Renders the appropriate input field based on parameter type
   * Handles different input requirements for each type (boolean, number, array, etc.)
   */
  const renderDefaultValueInput = () => {
    switch (parameterForm.type) {
      case 'boolean':
        return (
          <select
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={parameterForm.defaultValue}
            onChange={(e) => handleFormChangeWithLiveUpdate({ defaultValue: e.target.value })}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        );
      
      case 'integer':
      case 'float':
        return (
          <input
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="number"
            value={parameterForm.defaultValue}
            onChange={(e) => handleFormChangeWithLiveUpdate({ defaultValue: e.target.value })}
            placeholder={`Enter ${parameterForm.type} value`}
            step={parameterForm.type === 'float' ? '0.1' : '1'}
          />
        );
      
      case 'array':
      case 'object':
        return (
          <>
            <textarea
              className={`w-full border ${defaultValueError ? 'border-red-500 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              value={parameterForm.defaultValue}
              onChange={(e) => handleFormChangeWithLiveUpdate({ defaultValue: e.target.value })}
              placeholder={parameterForm.type === 'array' 
                ? 'Enter JSON array e.g. ["value1","value2"]'
                : 'Enter JSON object e.g. {"key": "value"}'}
              rows={3}
            />
            {defaultValueError && (
              <div className="text-sm text-red-600 dark:text-red-400 mt-1">{defaultValueError}</div>
            )}
          </>
        );
      
      case 'datetime':
        return (
          <input
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="datetime-local"
            value={parameterForm.defaultValue}
            onChange={(e) => handleFormChangeWithLiveUpdate({ defaultValue: e.target.value })}
          />
        );
      
      default: // string
        return (
          <>
            <textarea
              className={`w-full border ${defaultValueError ? 'border-red-500 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y`}
              value={parameterForm.defaultValue}
              onChange={(e) => handleFormChangeWithLiveUpdate({ defaultValue: e.target.value })}
              placeholder="Enter default value"
              rows={3}
            />
            {defaultValueError && (
              <div className="text-sm text-red-600 dark:text-red-400 mt-1">{defaultValueError}</div>
            )}
          </>
        );
    }
  };

  return (
    <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
      {/* Cards for existing parameters */}
      <div className="space-y-4 w-full">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Existing Parameters</h3>
        {Object.entries(policy.properties.parameters).map(([name, param]) => (
          <div key={name} className="border border-gray-200 dark:border-gray-700 rounded p-4">
            {editingParameter === name ? (
              <div>
                {/* Inline edit form fields for this parameter */}
                <div className="flex flex-col space-y-4">
                  {/* Name */}
                  <div className="flex flex-col">
                    <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Name</label>
                    <input
                      value={parameterForm.name}
                      onChange={(e) => handleFormChangeWithLiveUpdate({ name: e.target.value })}
                      className={`w-full border ${nameError ? 'border-red-500 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {nameError && <div className="text-red-600 text-xs">{nameError}</div>}
                  </div>
                  {/* Type, Description, Allowed Values, Default Value - reuse existing form inputs */}
                  {/* Display Name */}
                  <div className="flex flex-col">
                    <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Display Name</label>
                    <input
                      value={parameterForm.displayName}
                      onChange={(e) => handleFormChangeWithLiveUpdate({ displayName: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {/* Type */}
                  <div className="flex flex-col">
                    <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Type</label>
                    <select
                      value={parameterForm.type}
                      onChange={(e) => handleFormChangeWithLiveUpdate({ type: e.target.value, defaultValue: '', allowedValues: [] })}
                      className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="string">string</option>
                      <option value="array">array</option>
                      <option value="object">object</option>
                      <option value="boolean">boolean</option>
                      <option value="integer">integer</option>
                      <option value="float">float</option>
                      <option value="datetime">datetime</option>
                    </select>
                  </div>
                  {/* Description */}
                  <div className="flex flex-col">
                    <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Description</label>
                    <textarea
                      value={parameterForm.description}
                      onChange={(e) => handleFormChangeWithLiveUpdate({ description: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                    />
                  </div>
                  {/* Allowed Values */}
                  <div className="flex flex-col">
                    <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Allowed Values</label>
                    <textarea
                      value={parameterForm.allowedValues[0] || ''}
                      onChange={(e) => handleFormChangeWithLiveUpdate({ allowedValues: [e.target.value] })}
                      className={`w-full border ${allowedValuesError ? 'border-red-500 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y`}
                      rows={3}
                      placeholder="Enter allowed values as JSON array, e.g., [&quot;value1&quot;, &quot;value2&quot;]"
                    />
                    {allowedValuesError && <div className="text-red-600 text-xs">{allowedValuesError}</div>}
                  </div>
                  {/* Default Value */}
                  <div className="flex flex-col">
                    <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Default Value</label>
                    <div>{renderDefaultValueInput()}</div>
                  </div>
                  {/* Done Editing Button */}
                  <div className="flex justify-start pt-2">
                    <button
                      type="button"
                      onClick={(e) => onAddParameter(e)}
                      className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Done Editing
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>  {/* Static card view */}
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">{name}</h4>
                  {/* Action buttons */}
                  <div className="flex space-x-2 ml-4">
                    {/* Edit button - show for all parameters */}
                    <button
                      onClick={() => onEditParameter(name, param)}
                      title="Edit Parameter"
                      className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-semibold p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900"
                      aria-label="Edit Parameter"
                    >
                      ✏️
                    </button>
                    {/* Remove button - only show for non-effect parameters */}
                    {name !== 'effect' && (
                      <button
                        onClick={() => onRemoveParameter(name)}
                        title="Remove Parameter"
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-semibold p-1 rounded hover:bg-red-50 dark:hover:bg-red-900"
                        aria-label="Remove Parameter"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  <div>Type: {param.type}</div>
                  <div>Description: {param.metadata?.description || 'No description'}</div>
                  {param.allowedValues && (
                    <div>Allowed Values: {param.allowedValues.join(', ')}</div>
                  )}
                  <div>
                    Default Value:{' '}
                    {typeof param.defaultValue === 'object'
                      ? JSON.stringify(param.defaultValue)
                      : param.defaultValue}
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
        {/* Add Parameter button below cards */}
        <div className="flex justify-start mt-4">
          <button
            onClick={onAddClick}
            className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Add Parameter
          </button>
        </div>
      </div>
    </div>
  );
}; 